import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { ScreeningQuestionTypeEnum } from '@/app/common/general-types/briefs.dto';
import { MbaIcon } from '../../assignment/atoms/mba-icon/mba-icon';
import {
  expectedSummary,
  MAX_SCREENING_QUESTIONS,
  newScreeningDraft,
  OPERATOR_SYMBOL,
  OPERATOR_WORDS,
  plural,
  SCREENING_TYPE_LABEL,
  ScreeningDraft,
  screeningDraft,
  screeningPayload,
  sectionStatus,
  StoredScreeningQuestion,
} from '../briefing.helpers';
import { BriefingService } from '../briefing.service';
import { bf } from '../briefing-ui';
import { BriefingDrawer } from '../molecules/briefing-drawer/briefing-drawer';
import { BriefingSection } from '../molecules/briefing-section/briefing-section';

/**
 * Screening Questions: up to 3, answered on apply. A Yes/No or Number question with an expected
 * answer auto-qualifies matching applicants. Locked once anyone has applied, because answers are
 * judged against the questions when the brand reviews them (the API refuses too).
 */
@Component({
  selector: 'mbt-briefing-screening',
  imports: [BriefingSection, BriefingDrawer, MbaIcon],
  template: `
    <mbt-briefing-section key="scr" icon="list-checks" title="Screening Questions" [flash]="flash()"
      helper="Up to 3 questions creators answer when they apply. Set an expected answer to auto-qualify applicants."
      [status]="status()" (edit)="open()">
      @if (status() === 'missing') {
        <div [class]="bf.empty">
          <span class="flex min-w-0 flex-1 flex-col gap-0.5">
            <b [class]="bf.emptyTitle">No screening questions</b>
            <span [class]="bf.sub">Ask up to 3 questions to filter applicants before you review them.</span>
          </span>
          @if (!locked()) {
            <button type="button" [class]="bf.btnPrimary" (click)="open()"><mba-icon name="plus" size="size-4" />Set up</button>
          }
        </div>
      } @else {
        <ol class="m-0 list-none overflow-hidden rounded-lg border border-(--border-subtle) p-0">
          @for (q of questions(); track $index) {
            <li class="flex gap-3.5 border-b border-(--border-subtle) px-4 py-3.5 last:border-0">
              <span class="font-(family-name:--font-display) text-2xl leading-none font-extrabold text-(--app-accent)">{{ $index + 1 }}</span>
              <span class="flex min-w-0 flex-col gap-2">
                <b [class]="bf.display + ' text-[19px]'">{{ q.question }}</b>
                <span class="flex flex-wrap items-center gap-2">
                  <span class="rounded-full border border-(--border-subtle) bg-(--bg-alt) px-2.5 py-0.5 text-xs font-bold">{{ typeLabel[q.type] }}</span>
                  @if (expected(q); as text) {
                    <span class="inline-flex items-center gap-1 rounded-full bg-(--app-accent-soft) px-2.5 py-0.5 text-xs font-bold text-(--app-accent-strong)"><mba-icon name="check" size="size-3" [strokeWidth]="2.5" />{{ text }}</span>
                  } @else {
                    <span class="text-xs text-(--fg-3)">{{ q.type === ScreeningQuestionTypeEnum.OneLine ? 'Reviewed manually' : 'No expected answer · reviewed manually' }}</span>
                  }
                </span>
              </span>
            </li>
          }
        </ol>
        <div [class]="bf.info"><mba-icon name="info" size="size-4" /><span>{{ questions().length }} of 3 questions. Creators answer these when they apply.</span></div>
      }
      @if (locked()) {
        <div [class]="bf.infoLock"><mba-icon name="lock" size="size-4" /><span>Screening is locked because {{ appliedText() }} applied and answered these questions.</span></div>
      }
    </mbt-briefing-section>

    @if (editing()) {
    <mbt-briefing-drawer icon="list-checks" title="Screening Questions" [mode]="status() === 'missing' ? 'Set up' : 'Edit'"
      helper="Up to 3 questions creators answer when they apply. Set an expected answer to auto-qualify applicants."
      [saving]="saving()" [canSave]="!locked()" (closed)="editing.set(false)" (save)="save()">
      @if (locked()) {
        <div [class]="bf.infoLock"><mba-icon name="lock" size="size-4" /><span>Screening is locked because {{ appliedText() }} applied and answered these questions.</span></div>
      }
      @for (q of drafts(); track $index; let i = $index) {
        <fieldset class="m-0 flex flex-col gap-3 rounded-lg border border-(--border-subtle) p-4" [disabled]="locked()">
          <legend class="sr-only">Question {{ i + 1 }}</legend>
          <div class="flex items-center gap-2">
            <span class="font-(family-name:--font-display) text-xl leading-none font-extrabold text-(--app-accent)">{{ i + 1 }}</span>
            <b class="font-(family-name:--font-display) text-xl leading-none font-extrabold">Question {{ i + 1 }}</b>
            <span class="text-[13px] text-(--fg-3)">· {{ typeLabel[q.type] }}</span>
            <span class="flex-1"></span>
            <button type="button" [class]="bf.iconButton" [attr.aria-label]="'Remove question ' + (i + 1)" (click)="remove(i)"><mba-icon name="trash-2" size="size-4" /></button>
          </div>
          <input type="text" [id]="'bf-q-' + i" [class]="bf.input" maxlength="300" placeholder="Ask creators a screening question…"
            [attr.aria-label]="'Question ' + (i + 1)" [value]="q.question" (input)="patch(i, { question: text($event) })" />
          <div class="flex flex-wrap items-center gap-3">
            <span [class]="bf.k" [id]="'bf-q-type-' + i">Answer type</span>
            <span [class]="bf.segTrack" role="radiogroup" [attr.aria-labelledby]="'bf-q-type-' + i">
              @for (type of types; track type) {
                <button type="button" role="radio" [attr.aria-checked]="q.type === type" [class]="q.type === type ? bf.segOn : bf.segOff"
                  (click)="patch(i, { type })">{{ typeLabel[type] }}</button>
              }
            </span>
          </div>
          <div class="flex flex-col gap-2 border-t border-(--border-subtle) pt-3">
            @switch (q.type) {
              @case (ScreeningQuestionTypeEnum.OneLine) {
                <div [class]="bf.info"><mba-icon name="info" size="size-4" /><span>Short text answers are reviewed manually, with no auto-check.</span></div>
              }
              @case (ScreeningQuestionTypeEnum.YesNo) {
                <span><b [class]="bf.fieldLabel">Expected answer</b> <span [class]="bf.fieldSub">· Optional. Skip to review manually</span></span>
                <span class="flex flex-wrap items-center gap-2.5">
                  <span [class]="bf.segTrack" role="radiogroup" [attr.aria-label]="'Expected answer for question ' + (i + 1)">
                    @for (answer of yesNo; track answer) {
                      <button type="button" role="radio" [attr.aria-checked]="q.expectedYesNo === answer" [class]="q.expectedYesNo === answer ? bf.segOn : bf.segOff"
                        (click)="patch(i, { expectedYesNo: q.expectedYesNo === answer ? '' : answer })">{{ answer }}</button>
                    }
                  </span>
                  @if (q.expectedYesNo) {
                    <span class="inline-flex items-center gap-1 rounded-full bg-(--app-accent-soft) px-2.5 py-0.5 text-xs font-bold text-(--app-accent-strong)"><mba-icon name="check" size="size-3" [strokeWidth]="2.5" />Auto-qualifies creators who answer {{ q.expectedYesNo }}</span>
                  }
                </span>
              }
              @case (ScreeningQuestionTypeEnum.Numerical) {
                <span><b [class]="bf.fieldLabel">Expected answer</b> <span [class]="bf.fieldSub">· Optional. Leave the number empty to review manually</span></span>
                <span class="flex flex-wrap items-center gap-2.5 text-[13px] text-(--fg-2)">
                  Applicant’s answer must be
                  <span [class]="bf.segTrack" role="radiogroup" [attr.aria-label]="'Comparison for question ' + (i + 1)">
                    @for (op of ops; track op) {
                      <button type="button" role="radio" [attr.aria-checked]="q.op === op" [attr.aria-label]="opWords[op]" [class]="q.op === op ? bf.segOn : bf.segOff"
                        (click)="patch(i, { op })">{{ opSymbol[op] }}</button>
                    }
                  </span>
                  <input type="number" inputmode="decimal" [class]="bf.input + ' w-[110px]'" placeholder="e.g. 5" [attr.aria-label]="'Expected number for question ' + (i + 1)"
                    [value]="q.value" (input)="patch(i, { value: text($event) })" />
                </span>
              }
            }
          </div>
        </fieldset>
      }
      @if (!locked()) {
        @if (drafts().length < max) {
          <button type="button" [class]="bf.addLink" (click)="add()"><mba-icon name="plus" size="size-4" />Add question</button>
        } @else {
          <span [class]="bf.sub">You’ve added the maximum of 3 questions.</span>
        }
      }
    </mbt-briefing-drawer>
    }
  `,
  host: { style: 'display: contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingScreening {
  protected readonly bf = bf;
  protected readonly ScreeningQuestionTypeEnum = ScreeningQuestionTypeEnum;
  protected readonly typeLabel = SCREENING_TYPE_LABEL;
  protected readonly types = [ScreeningQuestionTypeEnum.OneLine, ScreeningQuestionTypeEnum.YesNo, ScreeningQuestionTypeEnum.Numerical];
  protected readonly yesNo = ['Yes', 'No'] as const;
  protected readonly ops = ['gt', 'lt', 'eq'] as const;
  protected readonly opSymbol = OPERATOR_SYMBOL;
  protected readonly opWords = OPERATOR_WORDS;
  protected readonly max = MAX_SCREENING_QUESTIONS;
  private readonly svc = inject(BriefingService);

  readonly flash = input(false);

  protected readonly status = computed(() => sectionStatus('scr', this.svc.brief()!));
  protected readonly questions = computed<StoredScreeningQuestion[]>(() => this.svc.brief()?.screeningQuestions ?? []);
  protected readonly locked = computed(() => this.svc.applied() > 0);
  protected readonly appliedText = computed(() => {
    const n = this.svc.applied();
    return `${plural(n, 'creator')} ${n === 1 ? 'has' : 'have'}`;
  });

  protected readonly editing = signal(false);
  protected readonly saving = signal(false);
  protected readonly drafts = signal<ScreeningDraft[]>([]);

  protected expected(question: StoredScreeningQuestion): string | null {
    return expectedSummary(question);
  }

  protected text(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  open(): void {
    const drafts = this.questions().map(screeningDraft);
    this.drafts.set(drafts.length || this.locked() ? drafts : [newScreeningDraft()]);
    this.editing.set(true);
  }

  protected patch(index: number, change: Partial<ScreeningDraft>): void {
    this.drafts.update((list) => list.map((q, i) => (i === index ? { ...q, ...change } : q)));
  }

  protected remove(index: number): void {
    this.drafts.update((list) => list.filter((_, i) => i !== index));
  }

  protected add(): void {
    this.drafts.update((list) => [...list, newScreeningDraft()]);
    const id = `bf-q-${this.drafts().length - 1}`;
    setTimeout(() => document.getElementById(id)?.focus());
  }

  protected async save(): Promise<void> {
    if (this.drafts().some((q) => !q.question.trim())) {
      this.svc.error('Write every question, or remove empty ones');
      return;
    }
    this.saving.set(true);
    const ok = await this.svc.save(
      [() => this.svc.screeningQuestions(this.drafts().map(screeningPayload))],
      'Screening Questions saved',
    );
    this.saving.set(false);
    if (ok) {
      this.editing.set(false);
    }
  }
}
